import {
  type ChainFullId,
  ChainFullIds,
  type LedgerSigner,
  type SignTransactionOptions,
  isMultichainChainNamespace,
  normalizeSecretKey,
  parseChainFullId,
  useChainRegistry,
} from "@umami/chains";
import { useSelectedNetwork } from "@umami/state";
import { Bridge, type BridgeDirection, type BridgeTokenArgs } from "@umami/swap";
import { CustomError } from "@umami/utils";
import { HDNodeWallet, Wallet } from "ethers";
import { useCallback, useMemo } from "react";

export const useBridge = () => {
  const currentNetwork = useSelectedNetwork();
  const { registry } = useChainRegistry();

  const bridge = useMemo(() => {
    const network = currentNetwork.name === "mainnet" ? "mainnet" : "shadownet";

    return new Bridge({ network });
  }, [currentNetwork.name]);

  const createSigner = useCallback(
    async (
      options: SignTransactionOptions,
      chainId: ChainFullId = ChainFullIds.ETHERLINK
    ): Promise<Wallet | LedgerSigner | HDNodeWallet> => {
      if ("isLedger" in options && options.isLedger) {
        return await registry.getLedgerSigner(chainId, options.derivationPath);
      }

      let rpcProvider;
      switch (chainId) {
        case ChainFullIds.ETHEREUM:
          rpcProvider = bridge.ethereumProvider;
          break;
        case ChainFullIds.ETHERLINK:
          rpcProvider = bridge.etherlinkProvider;
          break;
        case ChainFullIds.BSC:
          rpcProvider = bridge.bscProvider;
          break;
        case ChainFullIds.TEZOS:
        default:
          throw new Error(`Unsupported chain ID: ${chainId}`);
      }

      if ("secretKey" in options && options.secretKey) {
        const { namespace } = parseChainFullId(chainId);

        if (!isMultichainChainNamespace(namespace)) {
          throw new CustomError(`Unsupported chain namespace: ${namespace}`);
        }
        const { multichain } = await normalizeSecretKey(options.secretKey);

        const secretKey = multichain[namespace];

        if (!secretKey) {
          throw new Error(`No secret key available for chain ${chainId}`);
        }

        return new Wallet(secretKey, rpcProvider);
      } else if ("mnemonic" in options && options.mnemonic) {
        const derivationPath = options.derivationPaths?.[chainId];

        if (!derivationPath) {
          throw new Error(`No derivation path available for chain ${chainId}`);
        }

        return HDNodeWallet.fromPhrase(options.mnemonic, "", derivationPath).connect(rpcProvider);
      } else {
        throw new Error("No valid signing options provided");
      }
    },
    [bridge, registry]
  );

  const estimateFee = useCallback(
    async (direction: BridgeDirection) => {
      try {
        const { nativeFee, zroFee } = await bridge.estimateFee({
          direction,
          useZro: false,
        });

        return { nativeFee, zroFee };
      } catch (error) {
        console.error("Error estimating bridge fee:", error);
        throw error;
      }
    },
    [bridge]
  );

  const prepareApproveToken = useCallback(
    async (params: {
      direction: BridgeDirection;
      tokenAddress: string;
      amount: bigint;
      options: SignTransactionOptions;
      chainId: ChainFullId;
    }) => {
      try {
        const signer = await createSigner(params.options, params.chainId);

        return await bridge.prepareApproveToken({
          direction: params.direction,
          tokenAddress: params.tokenAddress,
          amount: params.amount,
          signer,
        });
      } catch (error) {
        console.error("Error preparing token approval:", error);
        throw error;
      }
    },
    [bridge, createSigner]
  );

  const prepareBridgeToken = useCallback(
    async (
      params: Omit<BridgeTokenArgs, "signer"> & {
        options: SignTransactionOptions;
        chainId: ChainFullId;
      }
    ) => {
      try {
        const signer = await createSigner(params.options, params.chainId);

        return await bridge.prepareBridgeToken({
          ...params,
          signer,
        });
      } catch (error) {
        console.error("Error preparing bridge transaction:", error);
        throw error;
      }
    },
    [bridge, createSigner]
  );

  return {
    bridge,
    estimateFee,
    prepareApproveToken,
    prepareBridgeToken,
    createSigner,
  };
};
